/**
 * ManualAddCourseDialog — counsellor searches the WUA catalogue and
 * pins a specific course to a student's shortlist. The picked course
 * is saved with source='manual' + locked=true + starred=true, so it
 * survives regeneration and sorts above auto matches.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Search, Plus, Check, TrendingUp } from "lucide-react";
import { toast } from "sonner";


export function ManualAddCourseDialog({ cohortId, student, trigger, onAdded }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("UK");
  const [subject, setSubject] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [saving, setSaving] = useState(null);   // degree_id currently saving
  const [added, setAdded] = useState(new Set());  // ids we've already added
  const timer = useRef(null);
  const PAGE = 30;

  // Popular subject chips (data-verified: > 1400 rows each in DB).
  const SUBJECT_CHIPS = [
    "Business", "Computer Science", "Engineering", "Chemistry", "Physics",
    "Biology", "Mathematics", "Economics", "Psychology", "Law",
    "Accounting & Finance", "Nursing", "History", "Politics",
  ];

  useEffect(() => {
    if (!open) return;
    setResults([]);
    setTotal(0);
    setOffset(0);
    setAdded(new Set());
    setQ("");
    setSubject("");
  }, [open]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim() || q.trim().length < 2) { setResults([]); setTotal(0); setOffset(0); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/counsellors/degrees/search", {
          params: {
            q: q.trim(),
            country: country || undefined,
            subject: subject || undefined,
            limit: PAGE,
            offset: 0,
          },
        });
        setResults(r.data.results || []);
        setTotal(r.data.total || 0);
        setOffset(0);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Search failed");
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q, country, subject]);

  const loadMore = async () => {
    const nextOffset = offset + PAGE;
    setLoadingMore(true);
    try {
      const r = await api.get("/counsellors/degrees/search", {
        params: {
          q: q.trim(),
          country: country || undefined,
          subject: subject || undefined,
          limit: PAGE,
          offset: nextOffset,
        },
      });
      setResults((prev) => [...prev, ...(r.data.results || [])]);
      setOffset(nextOffset);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load more");
    } finally {
      setLoadingMore(false);
    }
  };

  const pin = async (row) => {
    setSaving(row.degree_id);
    try {
      await api.post(
        `/counsellors/cohorts/${cohortId}/students/${student.id}/shortlist/manual-add`,
        { degree_id: row.degree_id },
      );
      toast.success(`Added ${row.uni_name} · ${row.degree_name}`);
      setAdded((prev) => new Set([...prev, row.degree_id]));
      onAdded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add course");
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add a course for {student.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3" data-testid="manual-add-panel">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                autoFocus
                data-testid="manual-add-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder='Try "Warwick Chemistry" or "Harvard Biology"'
                className="pl-9"
              />
            </div>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              data-testid="manual-add-country"
              className="border border-stone-300 rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">All countries</option>
              <option value="UK">UK</option>
              <option value="US">US</option>
              <option value="CA">CA</option>
              <option value="AU">AU</option>
              <option value="IE">IE</option>
              <option value="NL">NL</option>
              <option value="DE">DE</option>
              <option value="FR">FR</option>
            </select>
          </div>

          {/* Subject chips — one-tap slice across popular disciplines */}
          <div className="flex flex-wrap gap-1.5" data-testid="manual-add-subject-chips">
            <button
              type="button"
              onClick={() => setSubject("")}
              data-testid="manual-add-subject-all"
              className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                subject === ""
                  ? "bg-stone-900 text-white border-stone-900"
                  : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
              }`}
            >
              All subjects
            </button>
            {SUBJECT_CHIPS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSubject(subject === s ? "" : s)}
                data-testid={`manual-add-subject-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                  subject === s
                    ? "bg-stone-900 text-white border-stone-900"
                    : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          <p className="text-[11px] text-stone-500 italic">
            Type at least 2 characters. Combine words to narrow (e.g. "Warwick Chemistry" or "Harvard Biology") —
            every word must appear on the course. Manual picks are pinned, starred and preserved across auto-shortlist regenerations.
          </p>

          {total > 0 && !loading && (
            <p className="text-[11px] text-stone-600" data-testid="manual-add-count">
              Showing <span className="font-medium">{results.length}</span> of{" "}
              <span className="font-medium">{total.toLocaleString()}</span> matches
              {total > results.length && (
                <>. Narrow with more keywords (e.g. "Chemistry Warwick") to pinpoint faster.</>
              )}
            </p>
          )}

          <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-1" data-testid="manual-add-results">
            {loading && <div className="text-xs text-stone-500 italic p-3">Searching…</div>}
            {!loading && q.length >= 2 && results.length === 0 && (
              <div className="text-xs text-stone-500 italic p-3 space-y-1">
                <p>No courses match all your keywords.</p>
                <p>Tip: Cambridge and Oxford teach many single sciences (like Chemistry, Physics or Biology) inside their broader Natural Sciences / Biomedical Sciences tripos. Try searching for the university name alone, or the tripos name.</p>
              </div>
            )}
            {results.map((r) => {
              const isAdded = added.has(r.degree_id);
              const showSubject = r.subject_area && r.degree_name && !r.degree_name.toLowerCase().includes(r.subject_area.toLowerCase());
              return (
                <div
                  key={r.degree_id}
                  data-testid={`manual-add-row-${r.degree_id}`}
                  className="border border-stone-200 rounded-md p-3 flex items-start justify-between gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{r.uni_name}</div>
                    <div className="text-sm text-stone-600">{r.degree_name}</div>
                    <div className="text-[11px] text-stone-500 mt-1 flex items-center gap-3 flex-wrap">
                      {r.country && <span className="font-mono">{r.country}</span>}
                      {r.qualification && <span>{r.qualification}</span>}
                      {showSubject && (
                        <span className="px-1.5 py-0.5 rounded bg-stone-100 text-stone-700">
                          Subject: {r.subject_area}
                        </span>
                      )}
                      {r.leo_median_5yr_gbp && (
                        <span className="inline-flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" /> £{r.leo_median_5yr_gbp.toLocaleString()} 5-yr
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={isAdded ? "ghost" : "outline"}
                    disabled={isAdded || saving === r.degree_id}
                    onClick={() => pin(r)}
                    data-testid={`manual-add-pin-${r.degree_id}`}
                    className={isAdded ? "text-emerald-700" : ""}
                  >
                    {isAdded
                      ? <><Check className="w-3 h-3 mr-1" /> Added</>
                      : <><Plus className="w-3 h-3 mr-1" /> Pin</>}
                  </Button>
                </div>
              );
            })}
            {total > results.length && !loading && (
              <div className="pt-2 flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadMore}
                  disabled={loadingMore}
                  data-testid="manual-add-load-more"
                >
                  {loadingMore ? "Loading…" : `Load ${Math.min(PAGE, total - results.length)} more`}
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
